import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Activity, BarChart3, Brain, AlertTriangle, Clock, ChevronRight, ExternalLink } from 'lucide-react';
import axios from 'axios';
import SEOHead, { getBreadcrumbSchema } from '../components/SEOHead';
import { getCompanyDescriptionWithFallback } from '../utils/companyDescriptions';
import { findStockSector } from '../utils/stockSectors';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * SEO-optimized page targeting "Why is {SYMBOL} down/up today?" queries
 * This page captures high-intent search traffic for stock movement questions
 */
const WhyIsItMoving = () => {
  const { symbol } = useParams();
  const upperSymbol = symbol?.toUpperCase() || '';
  
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStockData = async () => {
      if (!upperSymbol) return;
      
      setLoading(true);
      try {
        // Fetch overview data (includes price metrics, news, signals)
        const response = await axios.get(`${API_BASE}/api/stocks/${upperSymbol}/overview`, {
          timeout: 10000
        });
        setStockData(response.data);
      } catch (err) {
        console.error('Error fetching stock data:', err);
        setError('Unable to load stock data');
      } finally {
        setLoading(false);
      }
    };

    fetchStockData();
  }, [upperSymbol]);

  // Extract data
  const companyName = stockData?.price_metrics?.company_name || upperSymbol;
  const dailyReturn = stockData?.price_metrics?.daily_return;
  const volumeMultiple = stockData?.price_metrics?.volume_multiple;
  const activityLevel = stockData?.price_metrics?.activity_level;
  const news = stockData?.news_headlines || [];
  const institutionalSignal = stockData?.institutional_signal;
  const politicalSignal = stockData?.political_signal;
  
  // Determine direction
  const isUp = dailyReturn > 0;
  const isDown = dailyReturn < 0;
  const direction = isUp ? 'up' : isDown ? 'down' : 'flat';
  const directionText = isUp ? 'rising' : isDown ? 'falling' : 'moving';
  
  // Get company description and sector
  const companyDescription = getCompanyDescriptionWithFallback(upperSymbol, companyName);
  const sectorInfo = findStockSector(upperSymbol);
  
  // Generate dynamic title based on movement
  const dynamicTitle = isDown 
    ? `Why is ${upperSymbol} Stock Down Today?`
    : isUp 
      ? `Why is ${upperSymbol} Stock Up Today?`
      : `Why is ${upperSymbol} Stock Moving Today?`;
  
  // SEO meta description with keyword variations
  const seoDescription = `Discover why ${companyName} (${upperSymbol}) stock is ${directionText} today. Analyze ${upperSymbol} price changes, volume trends, and key market drivers. Get real-time insights on ${upperSymbol} stock movement.`;
  
  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": dynamicTitle,
        "description": seoDescription,
        "url": `https://scopethemarket.com/stocks/${upperSymbol}/why-is-it-moving`,
        "dateModified": new Date().toISOString(),
        "author": {
          "@type": "Organization",
          "name": "ScopeTheMarket"
        },
        "publisher": {
          "@type": "Organization",
          "name": "ScopeTheMarket",
          "url": "https://scopethemarket.com"
        },
        "about": {
          "@type": "Corporation",
          "name": companyName,
          "tickerSymbol": upperSymbol
        }
      },
      getBreadcrumbSchema([
        { name: "Home", url: "https://scopethemarket.com" },
        { name: "Stocks", url: "https://scopethemarket.com/stocks" },
        { name: upperSymbol, url: `https://scopethemarket.com/app/stock/${upperSymbol}` },
        { name: "Why Is It Moving", url: `https://scopethemarket.com/stocks/${upperSymbol}/why-is-it-moving` }
      ])
    ]
  };

  // Generate possible reasons based on data
  const generateReasons = () => {
    const reasons = [];
    
    // Volume-based reason
    if (volumeMultiple > 1.5) {
      reasons.push({
        icon: BarChart3,
        title: 'Unusual Trading Volume',
        description: `${upperSymbol} is trading at ${volumeMultiple.toFixed(1)}x its normal volume, indicating heightened investor interest.`,
        confidence: 'High'
      });
    } else if (volumeMultiple > 1.2) {
      reasons.push({
        icon: BarChart3,
        title: 'Above Average Volume',
        description: `Trading volume is ${Math.round((volumeMultiple - 1) * 100)}% above average, suggesting increased market attention.`,
        confidence: 'Medium'
      });
    }
    
    // Institutional activity
    if (institutionalSignal?.institutional_sentiment) {
      const sentiment = institutionalSignal.institutional_sentiment;
      reasons.push({
        icon: Activity,
        title: `Institutional ${sentiment === 'accumulation' ? 'Buying' : 'Selling'} Detected`,
        description: sentiment === 'accumulation' 
          ? `Large institutions appear to be accumulating ${upperSymbol} shares based on recent filings.`
          : `Institutional investors have been reducing positions in ${upperSymbol}.`,
        confidence: institutionalSignal.institutional_score > 60 ? 'High' : 'Medium'
      });
    }
    
    // Political trading
    if (politicalSignal?.trades_last_30d > 0) {
      reasons.push({
        icon: AlertTriangle,
        title: 'Congressional Trading Activity',
        description: `${politicalSignal.trades_last_30d} trades by members of Congress in the last 30 days, with ${politicalSignal.net_activity === 'net_buy' ? 'net buying' : 'net selling'} activity.`,
        confidence: 'Medium'
      });
    }
    
    // News-based reason
    if (news.length > 0) {
      reasons.push({
        icon: Brain,
        title: 'Recent News Coverage',
        description: `${news.length} recent news articles may be influencing ${upperSymbol}'s price movement today.`,
        confidence: 'Medium'
      });
    }
    
    // Sector/market reason (fallback)
    if (sectorInfo) {
      reasons.push({
        icon: TrendingUp,
        title: `${sectorInfo.sector} Sector Movement`,
        description: `${upperSymbol} may be moving in sympathy with broader ${sectorInfo.sector} sector trends.`,
        confidence: 'Low'
      });
    }
    
    // If no specific reasons, add general market reason
    if (reasons.length === 0) {
      reasons.push({
        icon: Activity,
        title: 'General Market Activity',
        description: `${upperSymbol}'s movement may be influenced by overall market conditions and investor sentiment.`,
        confidence: 'Low'
      });
    }
    
    return reasons;
  };

  const reasons = !loading && stockData ? generateReasons() : [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F1A' }}>
      <SEOHead
        title={`${dynamicTitle} | ScopeTheMarket`}
        description={seoDescription}
        canonical={`https://scopethemarket.com/stocks/${upperSymbol}/why-is-it-moving`}
        ogTitle={dynamicTitle}
        ogDescription={`Find out why ${companyName} (${upperSymbol}) stock is ${directionText}. Real-time analysis and market insights.`}
        structuredData={structuredData}
      />
      
      {/* Navigation Header */}
      <div className="border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors" style={{ color: '#64748B' }}>
              Home
            </Link>
            <ChevronRight className="w-4 h-4" style={{ color: '#475569' }} />
            <Link to="/stocks" className="hover:text-white transition-colors" style={{ color: '#64748B' }}>
              Stocks
            </Link>
            <ChevronRight className="w-4 h-4" style={{ color: '#475569' }} />
            <Link to={`/app/stock/${upperSymbol}`} className="hover:text-white transition-colors" style={{ color: '#64748B' }}>
              {upperSymbol}
            </Link>
            <ChevronRight className="w-4 h-4" style={{ color: '#475569' }} />
            <span style={{ color: '#F8FAFC' }}>Why Is It Moving</span>
          </nav>
        </div>
      </div>
      
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Back Link */}
        <Link 
          to={`/app/stock/${upperSymbol}`}
          className="inline-flex items-center gap-2 text-sm mb-6 hover:text-white transition-colors"
          style={{ color: '#64748B' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {upperSymbol} Stock Analysis
        </Link>
        
        {/* Hero Section with H1 */}
        <header className="mb-8">
          <h1 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: '#F8FAFC' }}
          >
            {dynamicTitle}
          </h1>
          
          {/* Company Info */}
          <p className="text-lg mb-2" style={{ color: '#94A3B8' }}>
            {companyName} ({upperSymbol})
          </p>
          
          {companyDescription && (
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#64748B' }}>
              {companyDescription}
            </p>
          )}
          
          {/* Current Price Movement */}
          {!loading && dailyReturn !== null && dailyReturn !== undefined && (
            <div 
              className="inline-flex items-center gap-3 px-4 py-3 rounded-lg"
              style={{ 
                backgroundColor: isDown ? 'rgba(248,113,113,0.1)' : isUp ? 'rgba(74,222,128,0.1)' : 'rgba(148,163,184,0.1)',
                border: `1px solid ${isDown ? 'rgba(248,113,113,0.2)' : isUp ? 'rgba(74,222,128,0.2)' : 'rgba(148,163,184,0.2)'}`
              }}
            >
              {isDown ? (
                <TrendingDown className="w-6 h-6" style={{ color: '#f87171' }} />
              ) : isUp ? (
                <TrendingUp className="w-6 h-6" style={{ color: '#4ade80' }} />
              ) : (
                <Activity className="w-6 h-6" style={{ color: '#94A3B8' }} />
              )}
              <div>
                <p className="text-2xl font-bold" style={{ color: isDown ? '#f87171' : isUp ? '#4ade80' : '#94A3B8' }}>
                  {dailyReturn >= 0 ? '+' : ''}{dailyReturn.toFixed(2)}%
                </p>
                <p className="text-xs" style={{ color: '#64748B' }}>
                  Today's Change
                </p>
              </div>
            </div>
          )}
        </header>
        
        {/* Loading State */}
        {loading && (
          <div className="py-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p style={{ color: '#64748B' }}>Analyzing {upperSymbol} stock movement...</p>
          </div>
        )}
        
        {/* Error State */}
        {error && !loading && (
          <div 
            className="p-6 rounded-xl text-center"
            style={{ backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)' }}
          >
            <p style={{ color: '#f87171' }}>{error}</p>
            <Link 
              to={`/app/stock/${upperSymbol}`}
              className="inline-flex items-center gap-2 mt-4 text-sm hover:underline"
              style={{ color: '#60A5FA' }}
            >
              View Full Stock Analysis <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        )}
        
        {/* Reasons Section */}
        {!loading && !error && (
          <>
            <section className="mb-10">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>
                Possible Reasons for {upperSymbol}'s Movement
              </h2>
              
              <div className="space-y-4">
                {reasons.map((reason, idx) => {
                  const Icon = reason.icon;
                  return (
                    <div 
                      key={idx}
                      className="p-5 rounded-xl"
                      style={{ 
                        backgroundColor: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.06)'
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}
                        >
                          <Icon className="w-5 h-5" style={{ color: '#60A5FA' }} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium" style={{ color: '#F8FAFC' }}>
                              {reason.title}
                            </h3>
                            <span 
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ 
                                backgroundColor: reason.confidence === 'High' ? 'rgba(74,222,128,0.1)' : reason.confidence === 'Medium' ? 'rgba(250,204,21,0.1)' : 'rgba(148,163,184,0.1)',
                                color: reason.confidence === 'High' ? '#4ade80' : reason.confidence === 'Medium' ? '#facc15' : '#94A3B8'
                              }}
                            >
                              {reason.confidence} Confidence
                            </span>
                          </div>
                          <p className="text-sm" style={{ color: '#94A3B8' }}>
                            {reason.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            
            {/* Recent News Section */}
            {news.length > 0 && (
              <section className="mb-10">
                <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>
                  Recent {upperSymbol} News
                </h2>
                <div className="space-y-3">
                  {news.slice(0, 5).map((article, idx) => (
                    <a
                      key={idx}
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-4 rounded-lg transition-colors hover:bg-white/5"
                      style={{ 
                        backgroundColor: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.06)'
                      }}
                    >
                      <p className="text-sm font-medium mb-1" style={{ color: '#F8FAFC' }}>
                        {article.title}
                      </p>
                      <div className="flex items-center gap-2 text-xs" style={{ color: '#64748B' }}>
                        <span>{article.source}</span>
                        {article.published_at && (
                          <>
                            <span>•</span>
                            <span>{article.published_at}</span>
                          </>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </section>
            )}
            
            {/* Keyword Variations for SEO (hidden visually but in DOM) */}
            <section className="mb-10">
              <h2 className="text-xl font-semibold mb-4" style={{ color: '#F8FAFC' }}>
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                <div 
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <h3 className="font-medium mb-2" style={{ color: '#F8FAFC' }}>
                    Why is {upperSymbol} {isDown ? 'falling' : isUp ? 'rising' : 'moving'} today?
                  </h3>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>
                    {upperSymbol} is {isDown ? 'down' : isUp ? 'up' : 'moving'} {Math.abs(dailyReturn || 0).toFixed(2)}% today. 
                    {volumeMultiple > 1.2 ? ` Trading volume is ${Math.round((volumeMultiple - 1) * 100)}% above normal, indicating heightened investor activity.` : ''}
                    {' '}Use ScopeTheMarket's analysis tools to track {upperSymbol}'s price movements in real-time.
                  </p>
                </div>
                
                <div 
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <h3 className="font-medium mb-2" style={{ color: '#F8FAFC' }}>
                    Is {upperSymbol} a good buy right now?
                  </h3>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>
                    ScopeTheMarket provides data-driven insights, not financial advice. 
                    Review {upperSymbol}'s institutional activity, news sentiment, and technical indicators on our{' '}
                    <Link to={`/app/stock/${upperSymbol}`} className="text-blue-400 hover:underline">
                      full {upperSymbol} analysis page
                    </Link>{' '}
                    to make informed decisions.
                  </p>
                </div>
                
                <div 
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <h3 className="font-medium mb-2" style={{ color: '#F8FAFC' }}>
                    Why is {upperSymbol} stock {isDown ? 'dropping' : 'going up'}?
                  </h3>
                  <p className="text-sm" style={{ color: '#94A3B8' }}>
                    Stock movements can be influenced by company earnings, market sentiment, sector trends, and macroeconomic factors.
                    Check the reasons above for specific factors that may be affecting {upperSymbol} today.
                  </p>
                </div>
              </div>
            </section>
            
            {/* CTA to Full Analysis */}
            <section 
              className="p-6 rounded-xl text-center"
              style={{ 
                background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                border: '1px solid rgba(59,130,246,0.2)'
              }}
            >
              <h2 className="text-lg font-semibold mb-2" style={{ color: '#F8FAFC' }}>
                Get Complete {upperSymbol} Analysis
              </h2>
              <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                View real-time price charts, AI-powered insights, institutional activity, and more.
              </p>
              <Link
                to={`/app/stock/${upperSymbol}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors"
                style={{ backgroundColor: '#3B82F6', color: 'white' }}
              >
                Analyze {upperSymbol} Stock
                <ExternalLink className="w-4 h-4" />
              </Link>
            </section>
          </>
        )}
        
        {/* Timestamp for freshness signal */}
        <footer className="mt-8 pt-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 text-xs" style={{ color: '#52525b' }}>
            <Clock className="w-3 h-3" />
            <span>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
          </div>
          
          {/* Disclaimer */}
          <p className="mt-4 text-xs" style={{ color: '#52525b' }}>
            <strong>Disclaimer:</strong> ScopeTheMarket provides market research and intelligence only. 
            This is not financial advice. Past performance does not guarantee future results. 
            Always do your own research before making investment decisions.
          </p>
        </footer>
      </main>
    </div>
  );
};

export default WhyIsItMoving;
